import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ProductCard } from "@/components/ProductCard";
import { PLAY_STYLES, SAFETY_NOTE } from "@/config/kiddee";
import { PRODUCT } from "@/constants/testIds";

const Block = ({ title, children }) => (
  <div className="py-5 border-t border-[#F0EBE1]">
    <h2 className="font-heading font-bold text-base">{title}</h2>
    <div className="mt-2 text-sm sm:text-base text-stone-600 leading-relaxed">{children}</div>
  </div>
);

export const ProductSeo = ({ product }) => {
  useSeo({
    title: `${product.name} | ${product.kiddee_fav ? "Kiddee Approved | " : ""}PetTok Shop`,
    description: `${product.name} for $${Number(product.price).toFixed(2)}. ${(product.description || "").slice(0, 120)} Free US shipping over $50.`,
    path: `/product/${product.slug}`,
    image: product.image,
  });
  return null;
};

export const ProductDetails = ({ product }) => {
  const style = product.category === "toys" ? PLAY_STYLES.find((s) => s.id === product.shop_category) : null;
  return (
    <section data-testid={PRODUCT.details} className="mt-8">
      {style && (
        <Block title="Recommended play style">
          <Link to="/shop?cat=kiddee" className="font-semibold text-[#E06D44] hover:underline">{style.label}</Link>. {style.blurb}
        </Block>
      )}
      {[["materials", "Materials"], ["size", "Dimensions"], ["care", "Cleaning and care"], ["dog_size", "Dog size guidance"]]
        .filter(([k]) => product[k])
        .map(([k, label]) => <Block key={k} title={label}><span data-testid={PRODUCT.spec(k)}>{product[k]}</span></Block>)}
      <Block title="Safety and supervision">{SAFETY_NOTE}</Block>
      <Block title="Shipping and returns">
        Ships within 1–2 business days. Free US shipping on orders of $50 or more after discounts, $5.99 otherwise. Happy pets or your money back within 30 days.{" "}
        <Link to="/policies" className="underline text-[#E06D44]">Full policy</Link>
      </Block>
    </section>
  );
};

export const RelatedProducts = ({ product }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const cat = product.category === "toys" ? product.shop_category : product.category;
    api.get("/products", { params: { cat } })
      .then(({ data }) => {
        const more = data.filter((p) => p.slug !== product.slug);
        if (more.length >= 4) return more;
        return api.get("/products", { params: { cat: "kiddee" } }).then((r) => more.concat(r.data.filter((p) => p.slug !== product.slug && !more.some((m) => m.slug === p.slug))));
      })
      .then((list) => setItems(list.slice(0, 4)))
      .catch(() => setItems([]));
  }, [product]);
  if (!items.length) return null;
  return (
    <section data-testid={PRODUCT.related} className="mt-16 sm:mt-20">
      <h2 className="font-heading text-2xl font-extrabold tracking-tight">You might also like</h2>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {items.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
      </div>
    </section>
  );
};
